import { getStateAbbreviation } from '@liscio/common';

export type JurisdictionResult = {
  jurisdiction: string;
  isLocality: boolean;
};

/**
 * Parses jurisdiction info from a reviewable document prompt string.
 *
 * Prompt patterns:
 *   "Review Federal Return"                                          → { jurisdiction: 'US', isLocality: false }
 *   "Review Ohio Return"                                             → { jurisdiction: 'OH', isLocality: false }
 *   "Review Pennsylvania - Philadelphia - NPT (Net Profits) Return"  → { jurisdiction: 'PA', isLocality: true }
 *   "Review Ohio School District Return"                             → { jurisdiction: 'OH', isLocality: true }
 */
export const parseReviewItemJurisdiction = (
  prompt: string
): JurisdictionResult => {
  if (!prompt) return { jurisdiction: '', isLocality: false };

  const lowerPrompt = prompt.toLowerCase();

  // Check for Federal
  if (lowerPrompt.includes('federal')) {
    return { jurisdiction: 'US', isLocality: false };
  }

  // Check for locality pattern (e.g., "Review Pennsylvania - Philadelphia - NPT (Net Profits) Return")
  // Supports en-dash, em-dash, and flexible whitespace around the first dash.
  // Group 2 captures everything after the first dash up to " Return", including
  // additional hyphens in locality names like "Philadelphia - NPT (Net Profits)".
  const localityMatch = lowerPrompt.match(
    /review\s+([^\u2010-\u2015-]+)\s*[\u2010-\u2015-]\s*(.+)\s+return/i
  );
  if (localityMatch) {
    const stateName = localityMatch[1].trim();
    const stateAbbrev = getStateAbbreviation(stateName);
    return { jurisdiction: stateAbbrev || stateName, isLocality: true };
  }

  // Check for school district pattern (e.g., "Review Ohio School District Return")
  // Treated as a sub-item of the parent state, similar to city/locality forms.
  const schoolDistrictMatch = lowerPrompt.match(
    /review\s+(.+)\s+school\s+district\s+return/i
  );
  if (schoolDistrictMatch) {
    const stateName = schoolDistrictMatch[1].trim();
    const stateAbbrev = getStateAbbreviation(stateName);
    return { jurisdiction: stateAbbrev || stateName, isLocality: true };
  }

  // Check for state pattern (e.g., "Review Alabama Return", "Review Massachusetts Return")
  const stateMatch = lowerPrompt.match(
    /review\s+([^\u2010-\u2015-]+)\s+return/i
  );
  if (stateMatch) {
    const stateName = stateMatch[1].trim();
    const stateAbbrev = getStateAbbreviation(stateName);
    return { jurisdiction: stateAbbrev || stateName, isLocality: false };
  }

  return { jurisdiction: '', isLocality: false };
};
